import { ref, computed, watch, unref } from "vue";
import { useStore } from "vuex";
import { MarketingConsentService } from "@/services/marketing-consent.service";
import { transformConsents } from "@/utils/consent.utils";
import { CONSENT_TYPE } from "@/configs/constants";

const CONSENT_TYPE_ALIASES = {
  [CONSENT_TYPE.BOOSTER_CCS_FORM]: [CONSENT_TYPE.BOOSTER_CSS_FORM],
};

export function useRequiredConsents(consentType = CONSENT_TYPE.ONBOARDING, options = {}) {
  const fetchWithoutPendingMatch = options.fetchWithoutPendingMatch === true;
  const enabledOption = options.enabled;

  const isFeatureEnabled = () => {
    if (enabledOption === undefined) return true;
    return unref(enabledOption);
  };

  const store = useStore();
  const consents = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const resolvedConsentApiType = ref(null);

  const user = computed(() => store.getters.getMe);
  const pendingConsentTypes = computed(() => {
    if (Array.isArray(user.value?.pending_marketing_consent_types)) {
      return user.value.pending_marketing_consent_types;
    }

    return user.value?.user_has_required_marketing_consents === true ? [CONSENT_TYPE.ONBOARDING] : [];
  });
  const acceptedConsentTypes = computed(() => {
    const aliases = CONSENT_TYPE_ALIASES[consentType] || [];
    return [consentType, ...aliases];
  });

  const userHasRequiredConsents = computed(() => {
    if (!isFeatureEnabled()) return false;
    return acceptedConsentTypes.value.some((type) => pendingConsentTypes.value.includes(type));
  });

  const shouldFetchConsents = computed(() => {
    if (!user.value) return false;
    if (!isFeatureEnabled()) return false;
    if (fetchWithoutPendingMatch) {
      return true;
    }
    return acceptedConsentTypes.value.some((type) => pendingConsentTypes.value.includes(type));
  });

  const pendingRequiredConsents = computed(() => {
    return consents.value.filter((consent) => {
      const requiredQuestions = (consent.questions || []).filter((q) => q.required);
      if (requiredQuestions.length === 0) return false;

      return requiredQuestions.some((q) => {
        const isServerAnswered = q.answer !== null && q.answer !== undefined;
        if (isServerAnswered) {
          return q.answer?.accepted !== true;
        }
        return true;
      });
    });
  });

  const firstPendingConsent = computed(() => pendingRequiredConsents.value[0] ?? null);

  const hasPendingRequired = computed(() => pendingRequiredConsents.value.length > 0);

  const fetchConsents = async () => {
    if (!shouldFetchConsents.value) return;
    loading.value = true;
    error.value = null;
    resolvedConsentApiType.value = null;
    try {
      const typesToTry = acceptedConsentTypes.value;
      let resolvedConsents = [];
      let lastError = null;

      for (const type of typesToTry) {
        try {
          const response = await MarketingConsentService.getUserConsents(type);
          const transformed = transformConsents(response.data);
          if (transformed.length > 0) {
            resolvedConsents = transformed;
            resolvedConsentApiType.value = type;
            break;
          }
        } catch (err) {
          lastError = err;
        }
      }

      if (resolvedConsents.length === 0 && lastError) {
        throw lastError;
      }

      consents.value = resolvedConsents;
    } catch (err) {
      console.error("Error fetching required consents:", err);
      error.value = err.response?.data?.message || "Failed to load consents";
      consents.value = [];
    } finally {
      loading.value = false;
    }
  };

  watch(
    shouldFetchConsents,
    (needs) => {
      if (needs) {
        fetchConsents();
      } else {
        consents.value = [];
        resolvedConsentApiType.value = null;
      }
    },
    { immediate: true }
  );

  return {
    consents,
    loading,
    error,
    pendingRequiredConsents,
    firstPendingConsent,
    hasPendingRequired,
    userHasRequiredConsents,
    pendingConsentTypes,
    fetchConsents,
    resolvedConsentApiType,
    shouldFetchConsents,
  };
}
